// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// Feature Permissions Hook
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
// Component Imports
import { useState, ChangeEvent } from 'react'

// Bootstrap Imports
import InputGroup from 'react-bootstrap/InputGroup'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'

interface ToolbarActivationDateProps {
   size?: 'sm' | 'lg'
}

const MONTH_MAP: Record<string, number> = {
   January: 0,
   February: 1,
   March: 2,
   April: 3,
   May: 4,
   June: 5,
   July: 6,
   August: 7,
   September: 8,
   October: 9,
   November: 10,
   December: 11,
}

function parseDateString(dateStr: string): Date {
   const [year, month, day] = dateStr.split('-').map(Number)
   return new Date(year, month - 1, day)
}

function formatDate(date: Date): string {
   const year = date.getFullYear()
   const month = String(date.getMonth() + 1).padStart(2, '0')
   const day = String(date.getDate()).padStart(2, '0')
   return `${year}-${month}-${day}`
}

function isHoliday(date: Date, holidays: string[]): boolean {
   return holidays.some((h) => {
      const [monthName, dayStr] = h.split(' ')
      return (
         date.getMonth() === MONTH_MAP[monthName] &&
         date.getDate() === parseInt(dayStr)
      )
   })
}

function isWeekend(date: Date): boolean {
   const day = date.getDay()
   return day === 0 || day === 6
}

function isUnavailable(date: Date, holidays: string[]): boolean {
   return isWeekend(date) || isHoliday(date, holidays)
}

function getNextAvailableDate(from: Date, holidays: string[]): Date {
   const next = new Date(from)
   next.setDate(next.getDate() + 1)
   while (isUnavailable(next, holidays)) {
      next.setDate(next.getDate() + 1)
   }
   return next
}

function isPastCutoffMST(opportunityType: string): boolean {
   // Only apply cutoff for Expansion opportunities
   if (opportunityType !== 'Expansion') {
      return false
   }
   const now = new Date()
   const mstTime = new Date(now.getTime() - 7 * 60 * 60 * 1000)
   const hours = mstTime.getUTCHours()
   const minutes = mstTime.getUTCMinutes()
   return hours > 17 || (hours === 17 && minutes >= 45)
}

function getTodayString(): string {
   return formatDate(new Date())
}

export default function ToolbarActivationDate({
   size,
}: ToolbarActivationDateProps) {
   const state = useQuoteContext()
   const { getString } = state
   const { hasPermission } = useFeaturePermissions()
   const [popupMessage, setPopupMessage] = useState<string | null>(null)

   let holidays: string[] = []
   try {
      const raw = getString('product.ToolbarActivationDate.ExcludeDates')
      if (raw && raw.trim()) {
         holidays = raw.split(',').map((d) => d.trim())
      }
   } catch {
      holidays = []
   }

   function handleDateChange(e: ChangeEvent<HTMLInputElement>) {
      const selectedStr = e.target.value
      if (!selectedStr) {
         state.onActivationDateChange(e)
         return
      }

      if (hasPermission('UnlimitedActivationDateSelect', state.user_role)) {
         state.onActivationDateChange(e)
         return
      }

      const selectedDate = parseDateString(selectedStr)
      const todayStr = getTodayString()

      // Expansion opps (with a next invoice date) allow weekends/holidays up to the next
      // invoice date, so skip auto-advancing a weekend/holiday selection to the next
      // available business day — the date the user picked is kept as-is.
      const allowWeekendsAndHolidays =
         state.opportunityType === 'Expansion' && !!state.nextInvoiceDate

      if (isUnavailable(selectedDate, holidays) && !allowWeekendsAndHolidays) {
         const nextDate = getNextAvailableDate(selectedDate, holidays)
         const nextStr = formatDate(nextDate)
         setPopupMessage(
            getString('product.ToolbarActivationDate.Modal.UnavailableDate'),
         )
         state.onActivationDateChange({
            target: { value: nextStr },
         } as ChangeEvent<HTMLInputElement>)
         return
      }

      if (selectedStr === todayStr && isPastCutoffMST(state.opportunityType)) {
         const nextDate = getNextAvailableDate(selectedDate, holidays)
         const nextStr = formatDate(nextDate)
         setPopupMessage(
            getString('product.ToolbarActivationDate.Modal.AfterHoursDate'),
         )
         state.onActivationDateChange({
            target: { value: nextStr },
         } as ChangeEvent<HTMLInputElement>)
         return
      }

      state.onActivationDateChange(e)
   }

   if (
      hasPermission('ToolbarActivationDate', state.user_role) ||
      state.proratedActivationDetails.prorated == true
   ) {
      const today = new Date().toISOString().split('T')[0]
      let min: string | undefined = today
      let max: string | undefined = today
      if (hasPermission('UnlimitedActivationDateSelect', state.user_role)) {
         //set min and max to undefined if user has access to unlimited activation date
         min = undefined
         max = undefined
      } else {
         //determine what to limit activation date selection to
         let nextInvoice
         try {
            // Check if nextInvoiceDate is null, undefined, or empty
            if (!state.nextInvoiceDate) {
               nextInvoice = null
            } else {
               nextInvoice = new Date(state.nextInvoiceDate)
               // Account for MDT
               nextInvoice.setDate(nextInvoice.getDate() + 1)
               // Check if the date is valid
               if (isNaN(nextInvoice.getTime())) {
                  nextInvoice = null
               }
            }
         } catch {
            nextInvoice = null
         }

         if (hasPermission('TwoWeeksActivationDateSelect', state.user_role)) {
            if (nextInvoice) {
               //set min to last day of the previous month of the next invoice date and max to two weeks from current date if user has access to two weeks activation date
               min = new Date(
                  nextInvoice.getFullYear(),
                  nextInvoice.getMonth() - 1,
                  nextInvoice.getDate(),
               )
                  .toISOString()
                  .split('T')[0]
            } else {
               min = new Date(state.timeData.currentDate).toJSON().slice(0, 10)
            }
            // Set max to two weeks (14 days) from current date
            const maxDate = new Date(state.timeData.currentDate)
            maxDate.setDate(maxDate.getDate() + 14)
            max = maxDate.toISOString().split('T')[0]
         } else if (
            hasPermission('OneMonthActivationDateSelect', state.user_role)
         ) {
            if (nextInvoice) {
               //set min to last day of the previous month of the next invoice date and max to 30 days from current date if user has access to one month activation date AND the quote is for an extended term
               min = new Date(
                  nextInvoice.getFullYear(),
                  nextInvoice.getMonth() - 1,
                  nextInvoice.getDate(),
               )
                  .toISOString()
                  .split('T')[0]
            } else {
               min = new Date(state.timeData.currentDate).toJSON().slice(0, 10)
            }
            // Set max based on term length
            const maxDate = new Date(state.timeData.currentDate)
            const daysToAdd = state.termLength > 1 ? 30 : 14
            maxDate.setDate(maxDate.getDate() + daysToAdd)
            max = maxDate.toISOString().split('T')[0]
         } else if (!hasPermission('ToolbarActivationDate', state.user_role)) {
            // If user is not able to access Activation Date picker, set min and max to next invoice date
            if (nextInvoice) {
               min = new Date(
                  nextInvoice.getFullYear(),
                  nextInvoice.getMonth(),
                  nextInvoice.getDate(),
               )
                  .toISOString()
                  .split('T')[0]
               max = new Date(
                  nextInvoice.getFullYear(),
                  nextInvoice.getMonth(),
                  nextInvoice.getDate(),
               )
                  .toISOString()
                  .split('T')[0]
            } else {
               min = new Date(state.timeData.currentDate).toJSON().slice(0, 10)
               max = new Date(state.timeData.currentDate).toJSON().slice(0, 10)
            }
         } else {
            // If user has access to Activation Date picker, set min to last day of the previous month of the next invoice date and max to last day of next month
            if (nextInvoice) {
               min = new Date(
                  nextInvoice.getFullYear(),
                  nextInvoice.getMonth() - 1,
                  nextInvoice.getDate(),
               )
                  .toISOString()
                  .split('T')[0]
            } else {
               min = new Date(state.timeData.lastDayOfPreviousMonth)
                  .toJSON()
                  .slice(0, 10)
            }
            max = new Date(state.timeData.lastDayOfNextMonth)
               .toISOString()
               .split('T')[0]
         }

         // For Expansion opportunities, no one in this branch may select an activation
         // date beyond the next invoice date (Unlimited roles are handled above and keep
         // their unrestricted selection). The min is left as-is. Weekends/holidays up to
         // the next invoice date are allowed, so the cap is the next invoice date itself
         // even when it lands on one (handleDateChange skips the weekend/holiday advance).
         if (state.opportunityType === 'Expansion' && nextInvoice) {
            max = new Date(
               nextInvoice.getFullYear(),
               nextInvoice.getMonth(),
               nextInvoice.getDate(),
            )
               .toISOString()
               .split('T')[0]
         }
      }

      return (
         <>
            <InputGroup size={size}>
               <InputGroup.Text id='activationdateAddon'>
                  {getString('product.ToolbarActivationDate.InputText')}
               </InputGroup.Text>
               <Form.Control
                  type='date'
                  min={min}
                  max={max}
                  size={size}
                  value={state.activationDate}
                  onChange={handleDateChange}
               ></Form.Control>
            </InputGroup>
            <Modal
               show={popupMessage !== null}
               onHide={() => setPopupMessage(null)}
               centered
            >
               <Modal.Header closeButton>
                  <Modal.Title>Date Notice</Modal.Title>
               </Modal.Header>
               <Modal.Body>{popupMessage}</Modal.Body>
               <Modal.Footer>
                  <Button
                     variant='primary'
                     onClick={() => setPopupMessage(null)}
                  >
                     OK
                  </Button>
               </Modal.Footer>
            </Modal>
         </>
      )
   } else {
      return null
   }
}
